import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UploadCloud, FileText, ArrowRight, Check, AlertTriangle } from "lucide-react";
import { apiRequest } from "../utils/api";
import { ARTIFACT_STEPS, toWizardResult } from "./artifactAdapter";
import VaultPicker, { recordVaultVerification } from "../secure/VaultPicker";

// Style lookups for the step indicator (replaces nested ternaries)
const CIRCLE_STYLES = {
  done: "bg-green-500 border-green-500 text-white",
  active: "bg-blue-600 border-blue-600 text-white",
  todo: "bg-white border-gray-300 text-gray-500",
};

const LABEL_STYLES = {
  done: "text-green-600",
  active: "text-blue-600",
  todo: "text-gray-500",
};

// Step 1 of the shared verification wizard, artifact variant.
// Same layout as the document upload page; posts to /api/artifact/verify-file.
export default function VerifyArtifactUpload() {
  const navigate = useNavigate();
  const currentStep = 0;
  const fileInputRef = useRef(null);

  const [files, setFiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [showVault, setShowVault] = useState(false);
  const [notice, setNotice] = useState(null); // { title, text } for "no signature" cases

  const getStepState = (index) => {
    if (index < currentStep) return "done";
    if (index === currentStep) return "active";
    return "todo";
  };

  const pick = (list) => {
    const f = Array.from(list || []).slice(0, 1); // one artifact at a time
    if (!f.length) return;
    setNotice(null);
    setFiles(
      f.map((file) => ({
        file,
        name: file.name,
        size: (file.size / 1024 / 1024).toFixed(2) + " MB",
        date: new Date().toLocaleDateString(),
        url: URL.createObjectURL(file),
      }))
    );
  };

  // File chosen from Secure Vault (already unlocked by the backend)
  const handleVaultSelect = (doc) => {
    setNotice(null);
    setFiles([doc]);
  };

  const verify = async () => {
    if (files.length === 0) {
      alert("Please upload an artifact");
      return;
    }
    try {
      setVerifying(true);
      setNotice(null);

      const formData = new FormData();
      formData.append("file", files[0].file);

      // apiRequest sends the Authorization header, like the signing pages do.
      // It throws on a non-OK response, so errors are handled in the catch.
      const result = await apiRequest("/api/artifact/verify-file", {
        method: "POST",
        formData,
      });

      // Nothing on record to verify against: the wizard has no screen for
      // "unknown", so say so here instead of showing a misleading result.
      if (result.verification_status === "UNKNOWN" || result.verification_status === "UNSIGNED") {
        setNotice({
          title: "No signature found",
          text:
            result.message ||
            "This file has no signature on record. It was never signed here, or it was modified so it can no longer be matched.",
        });
        return;
      }

      // File came from Secure Vault: save the outcome on that file
      if (files[0].vaultFileId) {
        await recordVaultVerification(
          files[0].vaultFileId,
          result.verification_status === "VERIFIED" ? "verified" : "failed"
        );
      }

      navigate("/automatic-verification", {
        state: { uploadedFiles: files, verificationResult: toWizardResult(result) },
      });
    } catch (e) {
      console.error(e);
      alert(e.message || "Failed to verify artifact");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="w-full bg-white mx-auto">
      {/* STEPS */}
      <div className="flex items-center justify-between mb-2 h-[72px]">
        {ARTIFACT_STEPS.map((step, index) => {
          const state = getStepState(index);
          return (
            <div key={step} className="flex-1 flex flex-col items-center relative">
              {index !== ARTIFACT_STEPS.length - 1 && (
                <div
                  className={`absolute top-5 left-1/2 w-full h-[2px] ${
                    state === "done" ? "bg-green-500" : "bg-gray-300"
                  }`}
                />
              )}
              <div
                className={`z-10 w-10 h-10 rounded-full flex items-center justify-center text-xs font-semibold border-2 ${CIRCLE_STYLES[state]}`}
              >
                {state === "done" ? <Check size={12} /> : index + 1}
              </div>
              <p className={`mt-1 text-sm font-medium text-center hidden sm:block ${LABEL_STYLES[state]}`}>
                {step}
              </p>
            </div>
          );
        })}
      </div>

      {/* UPLOAD BOX */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); pick(e.dataTransfer.files); }}
        className={`border-2 border-dashed rounded-2xl py-4 px-3 sm:py-6 sm:px-6 w-full max-w-2xl mx-auto text-center transition ${
          dragging ? "border-blue-600 bg-blue-50" : "border-blue-300 hover:border-blue-600 hover:bg-blue-50"
        }`}
      >
        <div className="flex justify-center mb-2">
          <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-full bg-blue-100 flex items-center justify-center shadow-md mb-2">
            <UploadCloud className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-blue-600" />
          </div>
        </div>
        <p style={{ fontSize: "16px", fontWeight: 500, color: "#374151" }}>
          Drag &amp; Drop your signed artifact here
        </p>
        <p className="text-base font-normal text-gray-400 mt-2 mb-2">or</p>
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          onChange={(e) => pick(e.target.files)}
        />
        <div className="flex flex-wrap items-center justify-center gap-3 mt-3">
          <button
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center justify-center h-[44px] px-6 bg-[#2563EB] text-white text-sm font-medium rounded-[10px] hover:bg-[#1D4ED8] transition"
          >
            Browse Files
          </button>
          <button
            onClick={() => setShowVault(true)}
            className="inline-flex items-center justify-center h-[44px] px-6 bg-white text-[#2563EB] border border-[#2563EB] text-sm font-medium rounded-[10px] hover:bg-blue-50 transition"
          >
            Select from Secure Vault
          </button>
        </div>
        <p className="text-sm font-normal text-gray-400 mt-4">
          Upload the signed artifact (or the original file) you want to verify
        </p>
      </div>

      {notice && (
        <div className="max-w-2xl mx-auto mt-4 flex items-start gap-3 border border-orange-200 bg-orange-50 rounded-xl px-4 py-3">
          <AlertTriangle size={18} className="text-orange-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-orange-700">{notice.title}</p>
            <p className="text-sm text-orange-700/90">{notice.text}</p>
          </div>
        </div>
      )}

      {/* SELECTED FILE */}
      <div className="mt-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base sm:text-14px md:text-xl font-semibold text-gray-700">Selected Artifact</h3>
          <span className="text-sm text-gray-400">{files.length} File</span>
        </div>
        {files.length === 0 ? (
          <div className="border rounded-2xl text-center text-gray-400 bg-gray-50 py-3">No file uploaded yet</div>
        ) : (
          files.map((doc) => (
            <div
              key={doc.vaultFileId || doc.url || `${doc.name}-${doc.size}`}
              className="flex items-center justify-between border border-gray-200 rounded-xl px-3 py-2 mt-2"
            >
              <div className="flex items-center gap-4">
                <div className="bg-red-100 p-2 rounded-lg"><FileText className="text-red-500 w-4 h-4" /></div>
                <div className="flex flex-col">
                  <p className="text-[#111827] break-all">
                    {doc.name}
                    {doc.vaultFileId && (
                      <span className="ml-2 px-2 py-0.5 text-[10px] rounded-full bg-green-100 text-green-700 font-medium">
                        Secure Vault
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-[#4B5563]">{doc.size} • {doc.date}</p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* SECURE VAULT PICKER */}
      <VaultPicker
        open={showVault}
        purpose="verify"
        onClose={() => setShowVault(false)}
        onSelect={handleVaultSelect}
      />

      {/* FOOTER */}
      <div className="flex justify-between mt-4">
        <button
          onClick={() => { setFiles([]); setNotice(null); }}
          className="inline-flex items-center h-[44px] px-6 bg-gray-100 text-gray-600 rounded-[10px] hover:bg-gray-200 transition text-sm font-medium"
        >
          Cancel
        </button>
        <button
          onClick={verify}
          disabled={verifying}
          className="flex items-center gap-1.5 h-[44px] px-6 rounded-[10px] text-white text-sm font-medium bg-[#2563EB] hover:bg-[#1D4ED8] transition disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {verifying ? "Verifying..." : <>Next <ArrowRight size={14} /></>}
        </button>
      </div>
    </div>
  );
}